"use client";

import { useAuth } from "@/components/AuthContext";
import { UpgradeRecoveryCard } from "@/components/paywall/UpgradeRecoveryCard";
import { trackEventWithAttribution } from "@/lib/analytics/posthog-helpers";
import { createLogger } from "@/lib/logger";
import {
  type PaywallRecoveryRecord,
  type UpgradeObjectionReason,
  canShowUpgradeObjections,
  clearPendingPaywallRecovery,
  getBrowserReturnTo,
  getReturnRouteCategory,
  hasSeenPaywallRecoveryThisSession,
  isPaywallRecoveryRouteEligible,
  markPaywallRecoverySeenThisSession,
  markUpgradeObjectionsShown,
  readPendingPaywallRecovery,
} from "@/lib/paywall/recovery";
import { buildTrackedPaywallUrl } from "@/lib/paywallUrl";
import { withoutSchoolPrefix } from "@/lib/school-routes";
import { supabase } from "@/lib/supabaseClient";
import { usePathname, useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";

const logger = createLogger("PaywallExitRecoveryHost");

async function hasActiveRevenueCatAccess(email: string): Promise<boolean | null> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) return null;

    const response = await fetch(
      `/api/revenuecat/subscriptions?appUserId=${encodeURIComponent(email)}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        cache: "no-store",
      }
    );
    if (!response.ok) return null;
    const data = (await response.json()) as { subscribed?: boolean };
    return data.subscribed === true;
  } catch (error) {
    logger.warn("Could not verify subscription before showing paywall recovery", { error });
    return null;
  }
}

async function loadCurrentStreak(): Promise<number | null> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const response = await fetch("/api/streak", {
      cache: "no-store",
      headers: session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : undefined,
    });
    if (!response.ok) return null;
    const data = (await response.json()) as {
      success?: boolean;
      streak?: { currentStreak?: number };
    };
    return data.success && typeof data.streak?.currentStreak === "number"
      ? data.streak.currentStreak
      : null;
  } catch {
    return null;
  }
}

export function PaywallExitRecoveryHost() {
  const pathname = usePathname();
  const router = useRouter();
  const posthog = usePostHog();
  const { user, isLoading: authLoading } = useAuth();
  const [recovery, setRecovery] = useState<PaywallRecoveryRecord | null>(null);
  const [currentStreak, setCurrentStreak] = useState<number | null>(null);
  const [showObjections, setShowObjections] = useState(false);
  const processingIdRef = useRef<string | null>(null);

  useEffect(() => {
    setRecovery(null);
    setCurrentStreak(null);
    setShowObjections(false);
    processingIdRef.current = null;
  }, [user?.id]);

  useEffect(() => {
    if (authLoading || !pathname || withoutSchoolPrefix(pathname) === "/paywall") return;

    const pending = readPendingPaywallRecovery();
    if (!pending || processingIdRef.current === pending.id) return;
    processingIdRef.current = pending.id;

    if (
      !user?.id ||
      !user.email ||
      pending.ownerUserId !== user.id ||
      !isPaywallRecoveryRouteEligible(pathname, pending.source)
    ) {
      clearPendingPaywallRecovery();
      return;
    }

    if (hasSeenPaywallRecoveryThisSession(user.id)) {
      clearPendingPaywallRecovery();
      return;
    }

    let cancelled = false;
    void (async () => {
      const subscribed = await hasActiveRevenueCatAccess(user.email!);
      if (cancelled) return;

      // Fail closed if billing state cannot be verified. The pending record will expire naturally.
      if (subscribed === null) {
        processingIdRef.current = null;
        return;
      }
      if (subscribed) {
        clearPendingPaywallRecovery();
        return;
      }

      const objectionEligible =
        pending.userIntentSegment === "high_intent_returner" && canShowUpgradeObjections(user.id);

      markPaywallRecoverySeenThisSession(user.id);
      if (objectionEligible) {
        markUpgradeObjectionsShown(user.id);
      }
      clearPendingPaywallRecovery();
      setShowObjections(objectionEligible);
      setRecovery(pending);

      const analyticsContext = {
        source: pending.source,
        exit_method: pending.exitMethod,
        offer_mode: pending.offerMode,
        user_intent_segment: pending.userIntentSegment,
        coupon_applied: pending.couponApplied,
        return_route_category: getReturnRouteCategory(pending.returnTo),
      };

      if (!pending.exitEventTracked) {
        trackEventWithAttribution(posthog, "paywall_exit_detected", analyticsContext);
      }
      trackEventWithAttribution(posthog, "upgrade_recovery_viewed", {
        ...analyticsContext,
        recovery_surface: "paywall_exit_card",
        trigger: "paywall_exit",
        objection_eligible: objectionEligible,
        streak_bucket: "loading",
      });

      const streak = await loadCurrentStreak();
      if (!cancelled) setCurrentStreak(streak);
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, pathname, posthog, user]);

  if (!recovery || !user?.id || recovery.ownerUserId !== user.id) return null;

  const trackAction = (action: string, reason?: UpgradeObjectionReason) => {
    trackEventWithAttribution(posthog, "upgrade_recovery_action_clicked", {
      action,
      recovery_surface: "paywall_exit_card",
      trigger: "paywall_exit",
      source: recovery.source,
      offer_mode: recovery.offerMode,
      user_intent_segment: recovery.userIntentSegment,
      objection_reason: reason,
      return_route_category: getReturnRouteCategory(recovery.returnTo),
    });
  };

  const closeRecovery = (action: "continue_free" | "dismiss") => {
    trackAction(action);
    setRecovery(null);
  };

  const reopenPaywall = (reason?: UpgradeObjectionReason) => {
    trackAction(reason === "checkout_problem" ? "retry_checkout" : "upgrade", reason);
    setRecovery(null);
    router.push(
      buildTrackedPaywallUrl({
        extraParams: reason ? { recovery_reason: reason } : undefined,
        returnTo: getBrowserReturnTo(),
        source: "paywall_exit_recovery",
      })
    );
  };

  return (
    <UpgradeRecoveryCard
      currentStreak={currentStreak}
      onClose={closeRecovery}
      onObjectionSelected={(reason) => {
        trackEventWithAttribution(posthog, "upgrade_objection_selected", {
          reason,
          recovery_surface: "paywall_exit_card",
          source: recovery.source,
          user_intent_segment: recovery.userIntentSegment,
        });
      }}
      onUpgrade={reopenPaywall}
      showObjections={showObjections}
      surface="paywall_exit_card"
    />
  );
}
